// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import EleveService from '../../services/EleveService';
import LivreService from '../../services/LivreService';

const OffcanvasAddLivreForm = ({ onClick }) => {
	const [eleveOptions, setEleveOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedEleves, setSelectedEleves] = useState(null);

	useEffect(() => {
		const fetchEleveOptions = async () => {
			try {
				const eleves = await EleveService.getEleveOptions();
				setEleveOptions(eleves);
			} catch (error) {
				console.error('Error fetching students options', error);
			}
		};

		fetchEleveOptions();
	}, []);

	const STATUT_CHOICES = [
		{ value: 'D', label: 'Disponible' },
		{ value: 'E', label: 'Emprunté' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const livreData = new FormData();

				// Add all your form data to the FormData
				livreData.append('titre', form.titre.value);
				livreData.append('auteur', form.auteur.value);
				livreData.append('editeur', form.editeur.value);
				livreData.append('date_achat', form.date_achat.value);
				livreData.append('numero_appel', form.numero_appel.value);
				livreData.append('cout', form.cout.value);
				livreData.append('statut', form.statut.value);

				// Call the LivreService to create the livre
				const response = await LivreService.createLivre(livreData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of livres
				window.location.href = '/services/bibliotheque/catalogue';
			} catch (error) {
				console.error('Erreur lors de la création du livre', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				<Col xs={12} className="mb-3">
					{/* titre et auteur  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="titre">
									Titre <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="titre"
									type="text"
									placeholder="Saisir le titre du livre"
									id="titre"
									required
									isInvalid={!!feedbackMessages['titre']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											titre: e.target.value ? '' : 'Le titre est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['titre']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="auteur">
									Auteur <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="auteur"
									type="text"
									placeholder="Saisir l'auteur du livre"
									id="auteur"
									required
									isInvalid={!!feedbackMessages['auteur']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											auteur: e.target.value ? '' : "L'auteur est requis."
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['auteur']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* editeur */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="editeur">
							Editeur <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="editeur"
							type="text"
							placeholder="Saisir l'editeur du livre"
							id="editeur"
							required
							isInvalid={!!feedbackMessages['editeur']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									editeur: e.target.value ? '' : "L'editeur est requis."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['editeur']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* date_achat */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="date_achat">
							Date d'achat <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="date_achat"
							type="date"
							placeholder="Saisir la date d'achat du livre"
							id="date_achat"
							required
							isInvalid={!!feedbackMessages['date_achat']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									date_achat: e.target.value
										? ''
										: "La date d'achat est requise."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['date_achat']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				<Col xs={12} className="mb-3">
					{/* numero_appel et cout  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="numero_appel">
									Numero d'appel <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="numero_appel"
									type="number"
									placeholder="Saisir le Numero d'appel du livre"
									id="numero_appel"
									required
									isInvalid={!!feedbackMessages['numero_appel']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											numero_appel: e.target.value
												? ''
												: 'Le numero_appel est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['numero_appel']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="cout">Cout</Form.Label>
								<Form.Control
									name="cout"
									type="number"
									placeholder="Saisir le cout du livre"
									id="cout"
									required
									isInvalid={!!feedbackMessages['cout']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											cout: e.target.value ? '' : 'Le cout est requis.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['cout']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* date_achat */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="statut">
							Statut <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="statut"
							required
							isInvalid={!!feedbackMessages['statut']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									statut: e.target.value ? '' : 'Le statut du livre est requis.'
								}));
							}}
						>
							{STATUT_CHOICES.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['statut']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddLivreForm;
